import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CareerRoleProfileSkillMutationResponse,
  SkillDependencyRefDto,
  UpdateCareerRoleProfileSkillDependenciesRequest
} from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';
import { ProfileSkillOption } from './add-profile-skill-dialog.component';

export interface EditProfileSkillDependenciesDialogData {
  roleSlug: string;
  level: string;
  skillId: string;
  skillName: string;
  specializationSlug?: string;
  dependsOn?: SkillDependencyRefDto[];
  availableSkills?: ProfileSkillOption[];
}

@Component({
  selector: 'app-edit-profile-skill-dependencies-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSelectModule
  ],
  template: `
    <h2 mat-dialog-title>Edit skill dependencies — {{ data.skillName }}</h2>
    <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content class="content">
        <p class="hint">
          Select prerequisite skills on this {{ data.level }} profile. Leave empty to clear all skill dependencies.
        </p>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Depends on skills</mat-label>
          <mat-select formControlName="dependsOnSkillIds" multiple>
            <mat-option *ngFor="let skill of skillOptions" [value]="skill.id">{{ skill.name }}</mat-option>
          </mat-select>
        </mat-form-field>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="saving">Save</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .content { min-width: 420px; display: flex; flex-direction: column; gap: 0.35rem; }
      .full-width { width: 100%; }
      .hint { margin: 0 0 0.5rem; color: rgba(0, 0, 0, 0.6); font-size: 0.875rem; }
      .error { color: #b00020; }
    `
  ]
})
export class EditProfileSkillDependenciesDialogComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(
    MatDialogRef<EditProfileSkillDependenciesDialogComponent, CareerRoleProfileSkillMutationResponse | undefined>
  );
  readonly data = inject<EditProfileSkillDependenciesDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  saving = false;
  error = '';

  readonly skillOptions = (this.data.availableSkills ?? []).filter((s) => s.id !== this.data.skillId);

  readonly form = this.fb.nonNullable.group({
    dependsOnSkillIds: this.fb.nonNullable.control<string[]>(
      (this.data.dependsOn ?? [])
        .map((d) => d.skillId)
        .filter((id) => this.skillOptions.some((s) => s.id === id))
    )
  });

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.error = '';
    const request: UpdateCareerRoleProfileSkillDependenciesRequest = {
      dependsOn: (this.form.value.dependsOnSkillIds ?? []).map((skillId) => ({
        skillId,
        kind: 'hard'
      }))
    };
    try {
      const result = this.data.specializationSlug
        ? await firstValueFrom(
            this.api.updateCareerRoleFocusSkillDependencies(
              this.data.roleSlug,
              this.data.specializationSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          )
        : await firstValueFrom(
            this.api.updateCareerRoleCoreSkillDependencies(
              this.data.roleSlug,
              this.data.level,
              this.data.skillId,
              request
            )
          );
      this.dialogRef.close(result);
    } catch (error) {
      this.error = this.actions.errorMessage(error);
    } finally {
      this.saving = false;
    }
  }
}